import { useCallback, useRef, RefObject } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { IBlocksProps } from 'components/shared/ArrayFields/types';
import { PAGE_STATUS_APPROVED, PAGE_TYPE_PROPERTY } from 'constants/index';
import { useRect } from 'hooks/useRect';
import { closeModalAction, openModalAction } from 'store/modal';
import { isModalOpenedSelector, modalDataSelector } from 'store/modal/selectors';
import { getPageBlocks } from 'store/page/thunks';
import { accountIdSelector, isAgentSelector } from 'store/user/selectors';
import { axiosGetAutoCancelDecorator } from 'utils/axiosGetAutoCancelDecorator';
import { createOptionFromPage } from 'utils/createOptionFromPage';
import { decamelizeKeys } from 'utils/humps';
import { IPage } from 'utils/types';

import { IContentBlock } from '../../../types';
import { IMenuProps } from '../../types';
export function useScenePositioning(anchorRef: RefObject<Element>) {
  const sceneRef = useRef(null);
  const { top, left, width } = useRect(anchorRef);
  const menuPlacement = top + 270 > window.innerHeight ? 'top' : 'auto';

  return {
    sceneRef,
    sceneStyles: {
      top: top + 'px',
      left: left + 'px',
      width: width + 'px',
    },
    menuPlacement,
  };
}

const decoratedAxiosGet = axiosGetAutoCancelDecorator();

export function useContentBlockDropdown({ onAdd, onClose }: IMenuProps) {
  const dispatch = useDispatch();
  const accountId = useSelector(accountIdSelector);
  const isAgent = useSelector(isAgentSelector);
  const selectRef = useRef<HTMLElement>();
  const modalPreviewNameSpace = 'PAGE_PREVIEW';
  // @ts-ignore
  const isPreviewModalOpen = useSelector(isModalOpenedSelector(modalPreviewNameSpace));
  const modalData = useSelector(modalDataSelector);

  const openModal = useCallback(
    (pageId: string) => {
      dispatch(openModalAction({ type: modalPreviewNameSpace, data: { pageId } }));
    },
    [dispatch],
  );

  const closeModal = useCallback(() => {
    dispatch(closeModalAction());
    if (selectRef.current) {
      selectRef.current.focus();
    }
  }, [dispatch]);

  const loadOptions = useCallback(
    async (q: string) => {
      const response = await decoratedAxiosGet(`/acc/${accountId}/pages/dropdown`, {
        params: decamelizeKeys({
          q,
          limit: 100,
          statuses: [PAGE_STATUS_APPROVED],
          orderBy: 'handle',
          countless: '1',
        }),
      });

      return response.data.data.map((page: IPage) => createOptionFromPage(page, { isAgent }));
    },
    [accountId, isAgent],
  );

  const handleSelectPage = useCallback(
    async (pageId: string) => {
      const data: any = await dispatch(getPageBlocks(pageId));
      const blocks = getBlocksByPageType(data?.blocks || [], data.type).reduce((res: IContentBlock[], curBlock, i) => {
        const buf: IContentBlock = { ...curBlock };
        delete buf.id;
        buf.originalPageId = data.id;

        if (i === 0 && buf.type === 'header' && buf?.data?.subheadline) {
          buf.data.subheadline = data.name;
        }

        res.push(buf);

        return res;
      }, []);

      if (blocks.length > 0 && onAdd) {
        onAdd(blocks);
      }

      // use timeout to avoid setting state on unmounted component
      setTimeout(() => {
        onClose();
      }, 100);
    },
    [dispatch, onAdd, onClose],
  );

  return {
    isPreviewModalOpen,
    modalData,
    selectRef,
    handleSelectPage,
    loadOptions,
    openModal,
    closeModal,
  };
}

function getBlocksByPageType(blocks: IBlocksProps[], pageType: string) {
  if (pageType === PAGE_TYPE_PROPERTY) {
    const res = [];
    let sectionNameBlockPassed = false;

    for (let i = 0; i < blocks.length; i++) {
      if (blocks[i].type === 'header') {
        if (sectionNameBlockPassed) {
          break;
        }
        sectionNameBlockPassed = true;
      }

      res.push(blocks[i]);
    }

    return res;
  }

  return blocks;
}
